/* eslint-disable @typescript-eslint/no-explicit-any */
import DataTable from "@/components/data-table";
import { ColumnVisibility } from "@/components/data-table/column-visiblity";
import { DatePickerWithRange } from "@/components/date-range";
import { Button } from "@/components/ui/button";
import { IPlacementData } from "@/types/email-placement.types";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Circle, Download, Loader2 } from "lucide-react";
import { useRef, useState, useMemo } from "react";
import { mockPlacementHistoryData } from "./mock-placement-table-data";
import { cn } from "@/lib/utils";
import { mapToColorClasses } from "@/lib/colors";
import { DateRange } from "react-day-picker";
import {
  useGetTestHistory,
  usePublishInboxPlacementReport,
  useUnPublishInboxPlacementReport,
} from "@/hooks/use-inboxplacement";
import { PlacementType } from "@/types/inboxPlacements.types";
import { b2bColumnDefs, b2cColumnDefs } from "./placement-table.columns";
import { useServices } from "@/services/hooks/useServices";
import InboxPlacementPublishButton from "./inboxplacement-publish-button";
import { PublishLinksDialog } from "@/components/popups/publish-links-dialog";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface BreakdownData {
  mainStatus: string;
  breakdown?: Record<string, number>;
  totalCount: Record<string, number>;
}

const b2cProviderList = [
  "gmail",
  "outlook",
  "yahoo",
  "aol",
  "icloud",
  "gmx",
  "protonmail",
] as const;

const b2bProviderList = [
  "microsoft365",
  "googleWorkspace",
  "zohoWorkmail",
] as const;

export const PlacementCell = (props: { value: any }) => {
  const data = props.value;

  // If no data, show empty cell
  if (!data) {
    return <div className="h-full flex items-center">-</div>;
  }

  // For the new API structure with status and deliveryRate
  if (data.status) {
    const statusText = data.status.toUpperCase();
    const deliveryRate = data.deliveryRate;
    const category = data.category ? ` (${data.category})` : "";

    return (
      <div className="h-full flex items-center">
        <div className="flex items-center text-xs font-medium">
          <span
            className={cn(
              "px-2 py-1 rounded-l-md",
              statusText === "PARTIAL_INBOX"
                ? "bg-yellow-100 text-yellow-800"
                : mapToColorClasses(data.status.toLowerCase(), ["bg", "text"])
            )}
          >
            {statusText}
          </span>
          <span
            className={cn("px-2 py-1 rounded-r-md bg-slate-100 text-slate-500")}
          >
            {deliveryRate}%{category}
          </span>
        </div>
      </div>
    );
  }

  return <div className="h-full flex items-center">-</div>;
};

type Props = {
  reportId: string;
  email: string;
  type: PlacementType;
};

const PlacementHistoryTable = (props: Props) => {
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    to: new Date(),
    from: new Date(new Date().setDate(new Date().getDate() - 7)),
  });
  const [showPublishLinksDialog, setShowPublishLinksDialog] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isExporting, setIsExporting] = useState(false);

  const gridRefB2B = useRef<AgGridReact>(null);
  const gridRefB2C = useRef<AgGridReact>(null);
  const { inboxPlacementService } = useServices();
  const queryClient = useQueryClient();

  const { data: testHistory, isLoading } = useGetTestHistory(
    props.email,
    dateRange as DateRange,
    page,
    limit,
    props.type
  );

  const {
    mutateAsync: publishReport,
    data: publishedLinksData,
    isPending: isPublishing,
  } = usePublishInboxPlacementReport();

  const { mutateAsync: unPublishReport, isPending: isUnPublishing } =
    useUnPublishInboxPlacementReport();

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await inboxPlacementService.exportTestHistory(
        props.email,
        page,
        limit,
        props.type,
        dateRange?.from?.toISOString(),
        dateRange?.to?.toISOString()
      );
      const blob = new Blob([response as unknown as BlobPart], {
        type: "text/csv",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Inbox-placement-history.csv";
      a.click();
      toast.success("Report exported successfully");

      window.cioanalytics?.track("inbox_placement_history_exported", {
        email: props.email,
        type: props.type,
      });
    } catch (error) {
      toast.error("Failed to export report");
    } finally {
      setIsExporting(false);
    }
  };

  // Transform API response data to match grid format
  const transformedData = useMemo(() => {
    if (!testHistory?.success || !testHistory.tests?.length) return [];

    return testHistory.tests.map((test) => {
      // Create base row structure
      const row: any = {
        id: test.id,
        date: test.date,
        email: testHistory.senderEmail,
        type: test.type,
        score: test.score,
        isPublic: test.isPublic,
        publishedAt: test.publishedAt,
        companyId: test.companyId,
      };

      // Add provider results directly (no need for transformation since backend now returns the correct format)
      if (test.providerResults) {
        Object.entries(test.providerResults).forEach(([provider, data]) => {
          row[provider] = data;
        });
      }

      return row;
    });
  }, [testHistory]);

  const combinedb2bColumnDefs = useMemo<ColDef[]>(() => {
    return [
      ...b2bColumnDefs,
      {
        headerName: "Published",
        minWidth: 180,
        cellRenderer: (params: any) => {
          console.log(params.data, "params.data.companyId");
          return (
            <InboxPlacementPublishButton
              ids={[params.data.id]}
              companyId={params.data.companyId}
              email={props.email}
              isPublic={params.data.isPublic}
              type="history-table"
              reportType={PlacementType.B2B}
            />
          );
        },
      },
      {
        field: "publishedAt",
        headerName: "Published At",
        minWidth: 200,
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center h-full">
              {params.data.isPublic
                ? new Date(params.value).toLocaleString("en-US", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "N/A"}
            </div>
          );
        },
      },
    ];
  }, []);

  const combinedb2cColumnDefs = useMemo<ColDef[]>(() => {
    return [
      ...b2cColumnDefs,
      {
        headerName: "Published",
        minWidth: 180,
        cellRenderer: (params: any) => {
          console.log(params.data);
          return (
            <InboxPlacementPublishButton
              ids={[params.data.id]}
              email={props.email}
              companyId={params.data.companyId}
              isPublic={params.data.isPublic}
              type="history-table"
              reportType={PlacementType.B2C}
            />
          );
        },
      },
      {
        field: "publishedAt",
        headerName: "Published At",
        minWidth: 200,
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center h-full">
              {params.data.isPublic
                ? new Date(params.value).toLocaleString("en-US", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "N/A"}
            </div>
          );
        },
      },
    ];
  }, []);

  const handlePublish = async (flag: "publish" | "unpublish") => {
    try {
      console.log(selectedRows);
      const reportIds = selectedRows.map((row) => row.id);

      if (flag === "publish") {
        await publishReport({
          ids: reportIds,
        });
        setShowPublishLinksDialog(true);
        toast.success("Report published successfully");
      } else {
        await unPublishReport({
          ids: reportIds,
        });
        toast.success("Report unpublished successfully");
      }

      const eventName =
        flag === "publish"
          ? "inbox_placement_bulk_published"
          : "inbox_placement_bulk_unpublished";
      window.cioanalytics?.track(eventName, {
        count: reportIds.length,
        email: props.email,
        type: props.type,
      });

      setSelectedRows([]);
      gridRefB2B.current?.api.deselectAll();
      gridRefB2C.current?.api.deselectAll();

      queryClient.invalidateQueries({
        queryKey: ["inboxplacements-test-history", props.email, dateRange],
      });
    } catch (error) {
      toast.error("Failed to publish report");
    }
  };

  return (
    <div>
      {/* toolbar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-2">
        <span>
          {isLoading
            ? "Loading test history..."
            : `${transformedData.length} test(s) found`}
        </span>
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          {selectedRows.length > 0 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePublish("publish")}
                disabled={isPublishing}
              >
                <Circle className="w-4 h-4" />
                Publish
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePublish("unpublish")}
                disabled={isUnPublishing}
              >
                <Circle className="w-4 h-4" />
                Unpublish
              </Button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <DatePickerWithRange
              align="end"
              date={dateRange}
              onDateChange={(date) => setDateRange(date)}
            />

            <Button
              variant="outline"
              disabled={isLoading || !transformedData.length || isExporting}
              onClick={() => handleExport()}
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="hidden sm:block">Exporting...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:block">Export</span>
                </>
              )}
            </Button>
            <ColumnVisibility
              gridRef={
                props.type === PlacementType.B2B ? gridRefB2B : gridRefB2C
              }
              storageKey="inbox-placement-history"
              triggerClassName="[&>span]:hidden sm:[&>span]:block"
            />
          </div>
        </div>
      </div>

      <DataTable
        rowData={transformedData}
        columnDefs={
          props.type === PlacementType.B2B
            ? combinedb2bColumnDefs
            : combinedb2cColumnDefs
        }
        defaultColDef={{
          sortable: true,
          filter: true,
          editable: false,
          // minWidth: 180,
        }}
        loading={isLoading}
        onSelectionChanged={(event) => {
          setSelectedRows(event.api.getSelectedRows());
        }}
        ref={props.type === PlacementType.B2B ? gridRefB2B : gridRefB2C}
        rowHeight={60}
        height={550}
        className="w-full"
        showPagination={true}
        paginationProps={{
          currentPage: page,
          totalPages: testHistory?.total
            ? Math.ceil(testHistory.total / limit)
            : 1,
          totalEntries: testHistory?.total,
          pageSize: limit,
          onPageChange: (page: number) => setPage(page),
          onRowsPerPageChange: (limit: number) => setLimit(limit),
        }}
      />

      {publishedLinksData && (
        <PublishLinksDialog
          open={showPublishLinksDialog}
          onOpenChange={setShowPublishLinksDialog}
          links={publishedLinksData}
          type="inbox-placement"
        />
      )}

      {/* <DataTable
        ref={gridRef}
        rowData={transformedData}
        columnDefs={columnDefs}
        rowHeight={60}
        className="h-[500px] w-full"
        loading={isLoading}
      /> */}
    </div>
  );
};

export default PlacementHistoryTable;
